import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBcv } from '../context/BcvContext';
import { Package, TrendingUp, AlertTriangle, DollarSign, LogOut, Plus, Edit, Trash2, CheckCircle, RefreshCw, XCircle } from 'lucide-react';
import { SearchBar } from '../components/SearchBar';
import { AddProductModal } from '../components/AddProductModal';
import { EditProductModal } from '../components/EditProductModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getTotalStock = (tallas) => {
  if (!tallas || typeof tallas !== 'object') return 0;
  return Object.values(tallas).reduce((sum, v) => sum + (v || 0), 0);
};

export default function AdminPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, isLoading: authLoading, logout } = useAuth();
  const { rate: bcvRate, refresh: refreshBcv } = useBcv();
  const [bcvInput, setBcvInput] = useState('');
  const [bcvSyncing, setBcvSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sellSize, setSellSize] = useState({});

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin/login');
  }, [isAuthenticated, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      setIsLoading(true);
      const [statsRes, productsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, { 
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/products`)
      ]);

      if (statsRes.status === 401) { logout(); return; }

      if (statsRes.ok && productsRes.ok) {
        setStats(await statsRes.json());
        const pData = await productsRes.json();
        setProducts(pData || []);
        setFilteredProducts(pData || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  useEffect(() => {
    if (!searchQuery) { setFilteredProducts(products); return; }
    const q = searchQuery.toLowerCase();
    setFilteredProducts(products.filter(p =>
      p.descripcion.toLowerCase().includes(q) || (p.proveedor || '').toLowerCase().includes(q)
    ));
  }, [products, searchQuery]);

  const handleSell = async (productId) => {
    const size = sellSize[productId];
    if (!size) { toast.error('Selecciona talla'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/sale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_id: productId, size, quantity: 1 }),
      });
      if (res.ok) {
        toast.success('Vendido');
        fetchData();
      }
    } catch (err) { toast.error('Error'); }
  };

  const handleToggleAgotado = async (product) => {
    const nuevoEstado = !product.agotado;
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ agotado: nuevoEstado }),
      });
      if (res.ok) {
        toast.success(nuevoEstado ? 'Marcado como AGOTADO' : 'Marcado como disponible');
        fetchData();
      }
    } catch (err) { toast.error('Error al actualizar'); }
  };

  const handleRemovePromo = async (product) => {
    if (!window.confirm(`¿Quitar todas las promociones de "${product.descripcion}"?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ oferta: false, precio_oferta: null, descuento: null }),
      });
      if (res.ok) {
        toast.success('Promociones eliminadas');
        fetchData();
      }
    } catch (err) { toast.error('Error al quitar promociones'); }
  };

  const handleDelete = async (product) => {
    if (!window.confirm('¿Eliminar?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${product.id}`, { 
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Eliminado');
        fetchData();
      }
    } catch (err) { toast.error('Error'); }
  };

  const handleBcvUpdate = async () => {
    const rate = parseFloat(bcvInput);
    if (!rate || rate <= 0) { toast.error('Ingresa una tasa válida'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/bcv-rate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rate }),
      });
      if (res.ok) {
        toast.success('Tasa BCV actualizada');
        setBcvInput('');
        refreshBcv();
      } else {
        toast.error('Error al actualizar la tasa');
      }
    } catch { toast.error('Error de conexión'); }
  };

  const handleBcvSync = async () => {
    setBcvSyncing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/bcv-rate/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Tasa sincronizada: Bs. ${data.rate}`);
        refreshBcv();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'No se pudo sincronizar automáticamente');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setBcvSyncing(false); }
  };

  if (authLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[#39FF14]">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-gray-800 bg-black/50 p-4 sticky top-0 z-50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/fu-logo.png" alt="Logo" className="h-10 w-10 rounded-full border border-[#39FF14]" />
            <h1 className="font-black italic text-lg uppercase">ADMIN <span className="text-[#39FF14]">PANEL</span></h1>
          </div>
          <button onClick={() => { logout(); navigate('/'); }} className="text-gray-400 hover:text-white flex items-center gap-2">
            <LogOut className="w-4 h-4" /> SALIR
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8 space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-[#111] p-6 rounded-2xl border border-gray-800">
            <Package className="text-[#39FF14] mb-2" />
            <p className="text-xs text-gray-500 uppercase font-bold">Stock Total</p>
            <p className="text-3xl font-black">{stats?.total_stock || 0}</p>
          </div>
          <div className="bg-[#111] p-6 rounded-2xl border border-gray-800">
            <AlertTriangle className="text-[#FF9500] mb-2" />
            <p className="text-xs text-gray-500 uppercase font-bold">Stock Bajo</p>
            <p className="text-3xl font-black text-[#FF9500]">{stats?.low_stock_count || 0}</p>
          </div>
          <div className="bg-[#111] p-6 rounded-2xl border border-gray-800">
            <DollarSign className="text-[#39FF14] mb-2" />
            <p className="text-xs text-gray-500 uppercase font-bold">Valor</p>
            <p className="text-3xl font-black">${stats?.total_value?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="bg-[#111] p-6 rounded-2xl border border-gray-800">
            <TrendingUp className="text-[#39FF14] mb-2" />
            <p className="text-xs text-gray-500 uppercase font-bold">Modelos</p>
            <p className="text-3xl font-black">{stats?.total_products || 0}</p>
          </div>
        </div>

        {/* BCV Rate Section */}
        <div className="bg-[#111] rounded-3xl border border-[#39FF14]/20 overflow-hidden">
          <div className="p-6 border-b border-gray-800">
            <h2 className="font-black italic text-xl uppercase tracking-widest flex items-center gap-2">
              <DollarSign className="text-[#39FF14] w-5 h-5" /> Tasa <span className="text-[#39FF14]">BCV</span>
            </h2>
            <p className="text-xs text-gray-500 mt-1">Tasa oficial Banco Central de Venezuela usada para mostrar precios en Bs.</p>
          </div>
          <div className="p-6 flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase font-bold mb-1">Tasa Actual</p>
              <p className="text-3xl font-black text-[#39FF14]">
                {bcvRate ? `Bs. ${bcvRate.toLocaleString('es-VE', { minimumFractionDigits: 2 })}` : '—'}
              </p>
              <p className="text-[10px] text-gray-600 mt-0.5">por 1 USD</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <div className="flex gap-2 flex-1">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Ej: 46.50"
                  value={bcvInput}
                  onChange={(e) => setBcvInput(e.target.value)}
                  className="bg-black border border-gray-700 text-white text-sm rounded-lg px-3 py-2 flex-1 focus:border-[#39FF14] focus:outline-none"
                />
                <button
                  onClick={handleBcvUpdate}
                  className="bg-[#39FF14] text-black font-black px-5 py-2 rounded-lg text-xs uppercase shadow-[0_0_10px_rgba(57,255,20,0.3)] hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] transition-all"
                >
                  Guardar
                </button>
              </div>
              <button
                onClick={handleBcvSync}
                disabled={bcvSyncing}
                className="flex items-center gap-2 border border-gray-700 text-gray-300 hover:border-[#39FF14]/50 hover:text-white px-4 py-2 rounded-lg text-xs uppercase transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${bcvSyncing ? 'animate-spin' : ''}`} />
                {bcvSyncing ? 'Sincronizando...' : 'Sync Automático'}
              </button>
            </div>
          </div>
        </div>

        {/* Inventory Section */}
        <div className="bg-[#111] rounded-3xl border border-gray-800 overflow-hidden">
          <div className="p-6 border-b border-gray-800 flex justify-between items-center">
            <h2 className="font-black italic text-xl uppercase tracking-widest">Inventario</h2>
            <button 
              onClick={() => setIsAddModalOpen(true)}
              className="bg-[#39FF14] text-black font-black px-6 py-2 rounded-full text-xs uppercase shadow-[0_0_15px_rgba(57,255,20,0.3)]"
            >
              <Plus className="inline-block w-4 h-4 mr-1" /> Nuevo Producto
            </button>
          </div>
          
          <div className="p-6">
            <SearchBar value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery('')} placeholder="Buscar..." />
            
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-gray-500 text-[10px] uppercase tracking-widest border-b border-gray-800">
                    <th className="pb-4">Descripción</th>
                    <th className="pb-4">Stock Tallas</th>
                    <th className="pb-4 text-right">Precio</th>
                    <th className="pb-4 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {filteredProducts.map(product => {
                    const tallas = product.tallas || {};
                    const sizes = Object.entries(tallas).filter(([, s]) => s > 0);
                    return (
                      <tr key={product.id} className={`hover:bg-white/[0.02] ${product.agotado ? 'opacity-60' : ''}`}>
                        <td className="py-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-bold text-sm uppercase">{product.descripcion}</p>
                            {product.agotado && <span className="bg-gray-700 text-gray-300 text-[8px] font-black px-1.5 py-0.5 rounded uppercase border border-gray-600">AGOTADO</span>}
                            {product.oferta && <span className="bg-[#FF003C] text-white text-[8px] font-black px-1 rounded uppercase">OFERTA</span>}
                            {product.descuento > 0 && <span className="bg-[#39FF14] text-black text-[8px] font-black px-1 rounded">-{product.descuento}%</span>}
                          </div>
                          <p className="text-[10px] text-gray-500 uppercase">{product.categoria}</p>
                        </td>
                        <td className="py-4">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(tallas).map(([s, st]) => (
                              <span key={s} className={`text-[10px] px-1.5 py-0.5 rounded border ${st > 0 ? 'border-[#39FF14]/30 text-[#39FF14]' : 'border-red-500/30 text-red-500'}`}>
                                {s}:{st}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-4 text-right font-black text-[#39FF14]">
                          ${(product.precio || 0).toFixed(2)}
                        </td>
                        <td className="py-4">
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            <button onClick={() => { setSelectedProduct(product); setIsEditModalOpen(true); }} className="p-2 hover:text-[#39FF14] transition-colors"><Edit className="w-4 h-4" /></button>
                            <button
                              onClick={() => handleToggleAgotado(product)}
                              title={product.agotado ? 'Marcar disponible' : 'Marcar agotado'}
                              className={`p-2 transition-colors ${product.agotado ? 'text-gray-400 hover:text-[#39FF14]' : 'text-[#FF9500] hover:text-red-400'}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                            
                            <select 
                              value={sellSize[product.id] || ''} 
                              onChange={(e) => setSellSize({...sellSize, [product.id]: e.target.value})}
                              className="bg-black border border-gray-700 text-[10px] rounded p-1"
                            >
                              <option value="">Talla</option>
                              {sizes.map(([s, st]) => <option key={s} value={s}>{s} ({st})</option>)}
                            </select>

                            <button onClick={() => handleSell(product.id)} className="bg-[#39FF14] text-black p-1.5 rounded hover:scale-105 transition-transform"><CheckCircle className="w-4 h-4" /></button>

                            {(product.oferta || product.descuento > 0) && (
                              <button onClick={() => handleRemovePromo(product)} className="p-2 text-[#FF9500] hover:text-white transition-colors" title="Quitar Promoción">
                                <AlertTriangle className="w-4 h-4" />
                              </button>
                            )}
                            
                            <button onClick={() => handleDelete(product)} className="p-2 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <AddProductModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onProductAdded={fetchData} />
      {selectedProduct && (
        <EditProductModal isOpen={isEditModalOpen} onClose={() => { setIsEditModalOpen(false); setSelectedProduct(null); }} product={selectedProduct} onProductUpdated={fetchData} />
      )}
    </div>
  );
}
